import { IRole } from '@core/interfaces/role.interface';
import { TranslateService } from '@ngx-translate/core';
import { camelCase } from 'lodash';

const ROLE_NAME_TRANSLATION_KEY_PREFIX = 'pages.settings.userManagement.roles';

/**
 * Display name for a role from `GET /roles` (or any lightweight object with the same shape).
 *
 * A built-in role (`userGroupId` null/undefined) is translated: its id is snake_case
 * (`campaign`, `campaign_sub`) but ngx-translate keys have to be camelCase, so the id is
 * converted (`campaign_sub` -> `campaignSub`) before looking it up under
 * `pages.settings.userManagement.roles.*` (e.g. `campaign` -> "Admin", `campaignSub` -> "Member").
 * If that key has no translation yet, this falls back to the role's raw `name` rather than
 * showing the untranslated key.
 *
 * A custom (group-owned) role - `userGroupId` set - is never translated: its `name` is a
 * user-authored string, not a translation key, so it's returned as-is.
 */
export function translateRoleName(
  role: Pick<IRole, 'id' | 'name' | 'userGroupId'>,
  translate: TranslateService,
): string {
  if (role.userGroupId) {
    return role.name;
  }

  const key = `${ROLE_NAME_TRANSLATION_KEY_PREFIX}.${camelCase(role.id)}`;
  const translated = translate.instant(key);

  return translated === key ? role.name : translated;
}
